import type {
  Event,
  EventCategory,
  EventSeverity,
  KnownEventCategory,
  SiteRegion,
} from "@safers/api";

export const CCTV_EVENT_CATEGORIES: KnownEventCategory[] = ["DETECTION"];

export function isCctvEventCategory(category: EventCategory | undefined | null): boolean {
  if (category == null) return true;
  return (CCTV_EVENT_CATEGORIES as string[]).includes(category);
}

export type EventSeverityBucket =
  | "critical"
  | "warning"
  | "caution"
  | "attention"
  | "uncertain"
  | "normal";

const SEVERITY_BUCKET: Record<EventSeverity, EventSeverityBucket> = {
  CRITICAL: "critical",
  WARNING: "warning",
  CAUTION: "caution",
  ATTENTION: "attention",
  UNCERTAIN: "uncertain",
  NORMAL: "normal",
};

const SEVERITY_FALLBACK: EventSeverityBucket = "attention";

// `Pick<Event,"severity">` 로 좁히지 말 것 — REST `Event`(?: EventSeverity)와 실시간
// `EventMessage`(?: EventSeverity | null)가 같은 표에 섞인다.
export function eventSeverity(event: { severity?: EventSeverity | null }): EventSeverityBucket {
  if (!event.severity) return SEVERITY_FALLBACK;
  return SEVERITY_BUCKET[event.severity] ?? SEVERITY_FALLBACK;
}

export function isUncertainEvent(event: { severity?: EventSeverity | null }): boolean {
  return eventSeverity(event) === "uncertain";
}

// severity-* 는 @safers/ui globals.css 의 @theme 토큰에서 Tailwind 가 만드는 유틸리티다.
// 램프를 직접 지목(bg-yellow-200)하지 말 것 — 팔레트를 고쳐도 따라오지 않는다.
export const EVENT_SEVERITY_STYLES = {
  critical: { bg: "bg-severity-danger-200", text: "text-severity-danger-800", label: "위험" },
  warning: { bg: "bg-severity-warning-200", text: "text-severity-warning-800", label: "경고" },
  caution: { bg: "bg-severity-caution-200", text: "text-severity-caution-800", label: "주의" },
  attention: {
    bg: "bg-severity-attention-200",
    text: "text-severity-attention-800",
    label: "관심",
  },
  uncertain: {
    bg: "bg-severity-uncertain-200",
    text: "text-severity-uncertain-800",
    label: "불확실",
  },
  normal: { bg: "bg-severity-normal-200", text: "text-severity-normal-800", label: "정상" },
} as const;

export function severityChip(severity: EventSeverity | null | undefined) {
  return EVENT_SEVERITY_STYLES[eventSeverity({ severity: severity ?? undefined })];
}

export const EVENT_REGION_MAP: Record<SiteRegion, string> = {
  SEOUL: "서울",
  GYEONGGI_INCHEON: "경기",
  GANGWON: "강원",
  CHUNGCHEONG: "충청",
  JEOLLA: "전라",
  GYEONGSANG: "경상",
  JEJU: "제주",
};

export const EVENT_REGIONS = ["전체", ...Object.values(EVENT_REGION_MAP)] as const;
export type EventRegionTab = (typeof EVENT_REGIONS)[number];

export const EVENT_REGION_LABEL_TO_KEY: Record<string, SiteRegion> = Object.fromEntries(
  Object.entries(EVENT_REGION_MAP).map(([key, label]) => [label, key as SiteRegion])
);

export interface SiteEmergencyPayload {
  workerId: string;
  position: { lng: number; lat: number; altitude: number };
  vitals: { temperature: number; heartRate: number };
}

export interface SiteEvent extends Event {
  emergency?: SiteEmergencyPayload;
}
